import {
  Alert,
  AlertIcon,
  AlertTitle,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { getDisplayName } from 'types';
import { CurrencyLabel } from './Currency';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useAuthZ } from 'core';
import { AppInput } from './AppInput';
import { FormButtonGroup } from './FormButtonGroup';
import { toast } from 'toast';
import {
  DecisionType,
  TransactionType,
  useFetchSomeUsersQuery,
  usePostAfeTransactionDecisionMutation,
  usePostInvoiceTransactionDecisionMutation,
  usePostPurchaseOrderTransactionDecisionMutation,
  usePostWorkOrderTransactionDecisionMutation,
} from 'api';
import { PostDecisionRequest } from 'api/bindings/PostDecisionRequest';

// interface Form extends PostDecisionRequest & {entityId: bigint};
type Form = PostDecisionRequest & {
  entityId: string;
  transactionId: string;
  transactionType: TransactionType;
};

export interface DecisionDrawerProps extends Omit<DrawerProps, 'children'> {
  entityId: string;
  entityName: string;
  revisionName: string;
  transactionId: string;
  transactionType: TransactionType;
  ownerId: string;
  amount: string;
  onDecision?: (decision: Form) => void;
}

export const DecisionDrawer = (props: DecisionDrawerProps) => {
  const {
    entityId,
    entityName,
    transactionId,
    transactionType,
    amount,
    ownerId,
    revisionName,
    onDecision,
    ...rest
  } = props;

  const [userId] = useAuthZ();
  const { data: owner } = useFetchSomeUsersQuery(
    { ids: [ownerId] },
    { skip: !ownerId }
  );

  // const [postAfeTxnDecision] = usePostAfeTxnDecisionMutation();
  // const [postPurchaseOrderTxnDecision] =
  //   usePostPurchaseOrderTxnDecisionMutation();
  // const [postWorkOrderTxnDecision] = usePostWorkOrderTxnDecisionMutation();
  // const [postInvoiceTxnDecision] = usePostInvoiceTxnDecisionMutation();

  const [postAfeTxnDecision] = usePostAfeTransactionDecisionMutation();
  const [postPurchaseOrderTxnDecision] =
    usePostPurchaseOrderTransactionDecisionMutation();
  const [postWorkOrderTxnDecision] =
    usePostWorkOrderTransactionDecisionMutation();
  const [postInvoiceTxnDecision] = usePostInvoiceTransactionDecisionMutation();

  const entityInfo = useMemo(() => {
    switch (transactionType) {
      case TransactionType.Afe:
        return { name: 'AFE', post: postAfeTxnDecision };
      case TransactionType.PurchaseOrder:
        return { name: 'Purchase Order', post: postPurchaseOrderTxnDecision };
      case TransactionType.WorkOrder:
        return { name: 'Work Order', post: postWorkOrderTxnDecision };
      case TransactionType.Invoice:
        return { name: 'Invoice', post: postInvoiceTxnDecision };
      default:
        throw new Error(`unrecognized txn type ${transactionType}`);
    }
  }, [
    postAfeTxnDecision,
    postInvoiceTxnDecision,
    postPurchaseOrderTxnDecision,
    postWorkOrderTxnDecision,
    transactionType,
  ]);

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      entityId,
      transactionId,
      transactionType,
      userId: ownerId,
      comment: '',
      decisionType: DecisionType.Rejected,
    },
  });
  const { control, handleSubmit, watch } = form;

  const onSubmit = useCallback(
    async (f: Form) => {
      try {
        const result = await entityInfo.post(f);
        props.onClose();
        onDecision?.(f);
        toast({
          status: 'success',
          title: 'Decision Posted',
          description: `You have posted a decision of ${
            f.decisionType === DecisionType.Approved ? 'approval' : 'rejection'
          } for revision ${revisionName} on entity ${entityName}.`,
        });
        return result;
      } catch (err: any) {
        throw err;
      }
    },
    [entityInfo, entityName, onDecision, props, revisionName]
  );

  const decision = watch('decisionType');
  const alertDetails = useMemo(() => {
    return (
      <Alert status={decision === DecisionType.Approved ? 'success' : 'error'}>
        <AlertIcon />
        <AlertTitle>
          {(decision === DecisionType.Approved && (
            <Text>You are approving revision {revisionName}</Text>
          )) || <Text>You are rejecting revision {revisionName}</Text>}
        </AlertTitle>
      </Alert>
    );
  }, [decision, revisionName]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...form}>
        <Drawer size="lg" {...rest}>
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>
              <Heading size="xs">Review Revision: {revisionName}</Heading>
            </DrawerHeader>
            <DrawerBody>
              <Stack spacing={[2, 4]}>
                <SimpleGrid columns={2} gap={[2, 4]}>
                  <Text as="b">Entity:</Text>
                  <Text>{entityName}</Text>
                  <Text as="b">Type:</Text>
                  <Text>{entityInfo.name}</Text>
                  <Text as="b">Amount:</Text>
                  <CurrencyLabel value={amount} />
                </SimpleGrid>
                <Stack spacing={[2, 4]}>
                  <FormControl>
                    <FormLabel as="b" htmlFor="decision">
                      Approve
                    </FormLabel>
                    <Controller
                      control={control}
                      name="decisionType"
                      render={(p) => (
                        <Switch
                          id="decision"
                          size="lg"
                          isChecked={p.field.value === DecisionType.Approved}
                          onChange={() =>
                            p.field.onChange(
                              p.field.value === DecisionType.Approved
                                ? DecisionType.Rejected
                                : DecisionType.Approved
                            )
                          }
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel htmlFor="comments">Comments:</FormLabel>
                    <Controller
                      control={control}
                      name="comment"
                      rules={{
                        minLength: {
                          value: 3,
                          message:
                            'Comment must be between 3 and 256 in length.',
                        },
                        maxLength: {
                          value: 256,
                          message:
                            'Comment must be between 3 and 256 in length.',
                        },
                      }}
                      render={(p) => (
                        <AppInput
                          id="comments"
                          {...p.field}
                          value={p.field.value ?? ''}
                          multiple={true}
                        />
                      )}
                    />
                  </FormControl>
                </Stack>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <Stack gap={[2, 4]} w="100%">
                {alertDetails}
                {userId !== ownerId && owner?.items.length && (
                  <Alert status="info">
                    <AlertIcon />
                    <AlertTitle>
                      You are recording a decision on behalf of{' '}
                      {getDisplayName(owner[0])}
                    </AlertTitle>
                  </Alert>
                )}
                <FormButtonGroup
                  submitCaption={
                    decision === DecisionType.Approved ? 'Approve' : 'Reject'
                  }
                  disabledWhenClean={false}
                  onCancel={props.onClose}
                  onConfirm={handleSubmit(onSubmit)}
                />
              </Stack>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </FormProvider>
    </form>
  );
};
